export type LocationPositionMode = 'address' | 'latlng' | 'pin'

import type {CustomerContactRef, CustomerContactSource} from '../customers/schemas'
import type {ProjectBillingMethod} from '../projects/create-project-form-values'
import type {BuildOutScheduling} from '../service-schedules/build-out-types'
import type {PartialPickup} from '../work/partial-pickup-schema'
import type {BillableChargeType} from './schemas'
import type {JobStockEquipmentGroup} from './stock-equipment/schemas'

export type Customer = {
    id: string
    name: string
    slug: string
    contactName: string
    phone: string
    email?: string
}

export type CreateCustomerInput = {
    name: string
    slug?: string
    contactName: string
    phone: string
    email?: string
    title?: string
    description?: string
}

export type CustomerListFilters = {
    name?: string
}

export type JobSchedule = {
    startDate: string
    startTime: string | null
    endDate: string | null
    endTime: string | null
}

export type JobLifecycleStatus = 'estimate' | 'in_progress' | 'closed' | 'canceled'

export type JobLifecycle = {
    status: JobLifecycleStatus
    approvedToStart: boolean
    archivedAt: string | null
}

export type JobListItem = JobSchedule &
    JobLifecycle & {
        id: string
        name: string
        customerId: string
        customerName: string
        serviceTypeId: string
        serviceTypeName: string
        notes: string | null
        createdAt: string
        displayId: string
        poNumber: string | null
        projectBillingTermsId: string | null
        dateTbc?: boolean
        partialPickup?: PartialPickup | null
    }

export type JobListDateFilter = string | Date | null

export type JobListSortBy = 'created_at' | 'start_date'
export type JobListSortDir = 'asc' | 'desc'

export type JobListFilters = {
    mine?: boolean
    name?: string
    customerName?: string
    customerId?: string
    projectId?: string
    serviceTypeName?: string
    createdAfter?: JobListDateFilter
    createdBefore?: JobListDateFilter
    startDateFrom?: JobListDateFilter
    startDateTo?: JobListDateFilter
    status?: JobLifecycleStatus[]
    sortBy?: JobListSortBy
    sortDir?: JobListSortDir
}

export type JobLocation = {
    name: string
    notes: string
    positionMode: LocationPositionMode
    latitude: number | null
    longitude: number | null
    addressText: string | null
    fromSavedLocationId: string | null
    rememberAsSavedLocation: boolean
    savedLocationMode: 'create-new' | 'select-saved'
}

export type WorkerRequirementRow = {
    workerTypeId: string | null
    quantity: number
    estimatedHours: number | null
    billingRate?: string | null
    billingType?: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'flat' | 'per_load' | null
    /** Job number the rate was copied from. */
    rateFromJob?: string | null
}

export type AssetRequirementRow = {
    rentalAssetTypeId: string | null
    quantity: number
    billingRate: string | null
    billingType: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'flat' | 'per_load' | null
    rateFromJob?: string | null
}

export type DisposalFeeInput = {
    wasteType: string
    unit: string
    unitRate: number | null
    estimatedQuantity: number | null
}

export type BillingTermsFormValues = {
    transportationHourlyRate: number | null
    estimatedTransportationHours?: number | null
    flatRateAmount: number | null
    ratePerLoad: number | null
    perLoadNotes: string
    demurrageHourlyRate: number | null
    disposalFees: DisposalFeeInput[]
    washoutFee: number | null
    hseiPercent: number | null
    managerApprovedBelowMinimumRate: boolean
}

export type ManualBillableLineItemInput = {
    name: string
    description: string
    quantity: number | null
    unit: string
    unitPrice: number | null
}

export function createInitialBillingTermsFormValues(): BillingTermsFormValues {
    return {
        transportationHourlyRate: null,
        flatRateAmount: null,
        ratePerLoad: null,
        perLoadNotes: '',
        demurrageHourlyRate: null,
        disposalFees: [],
        washoutFee: null,
        hseiPercent: null,
        managerApprovedBelowMinimumRate: false,
    }
}

export function createEmptyWorkerRequirementRow(): WorkerRequirementRow {
    return {
        workerTypeId: null,
        quantity: 1,
        estimatedHours: null,
        billingRate: null,
        billingType: null,
    }
}

export function createEmptyAssetRequirementRow(): AssetRequirementRow {
    return {
        rentalAssetTypeId: null,
        quantity: 1,
        billingRate: null,
        billingType: null,
    }
}

export function createEmptyManualBillableLineItem(): ManualBillableLineItemInput {
    return {
        name: '',
        description: '',
        quantity: null,
        unit: 'each',
        unitPrice: null,
    }
}

export type CreateJobFormValues = {
    customerId: string | null
    projectId: string | null
    jobName: string
    poNumber: string
    serviceTypeId: string | null
    notes: string
    customerContactId: string | null
    startDate: Date | null
    endDate: Date | null
    startTime: string | null
    endTime: string | null
    workerRequirements: WorkerRequirementRow[]
    assetRequirements: AssetRequirementRow[]
    locations: JobLocation[]
    cloneJobId: string | null
    dispatchYardId?: string | null
    scheduling: BuildOutScheduling
    workMode: WorkMode
    projectBillingTermsId?: string | null
    internalContactId: string | null
    billingMethod: ProjectBillingMethod
    billingTerms: BillingTermsFormValues
    specificType?: string
    plannedEndDate?: Date | null
    quoteId?: string | null
    extraCharges?: ManualBillableLineItemInput[]
    yardPickedByRep?: boolean
    categoryPickedByRep?: boolean
}

type WorkMode = 'new' | 'existing'

export type CreateJobSummary = CreateJobFormValues & {
    customerName: string
    projectName: string | null
    persistedSchedule: JobSchedule
}

export function createEmptyLocation(): JobLocation {
    return {
        name: '',
        notes: '',
        positionMode: 'address',
        latitude: null,
        longitude: null,
        addressText: null,
        fromSavedLocationId: null,
        rememberAsSavedLocation: false,
        savedLocationMode: 'create-new',
    }
}

export function createInitialFormValues(): CreateJobFormValues {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return {
        customerId: null,
        projectId: null,
        jobName: '',
        poNumber: '',
        serviceTypeId: null,
        notes: '',
        customerContactId: null,
        startDate: null,
        endDate: null,
        startTime: null,
        endTime: null,
        workerRequirements: [],
        assetRequirements: [],
        locations: [],
        cloneJobId: null,
        dispatchYardId: null,
        scheduling: {
            pattern: 'one_day',
            startDate: today,
            endDate: today,
            startTime: null,
            endTime: null,
            cadence: {weekdays: [], everyNWeeks: 1},
            serviceTemplate: [],
            serviceTypeId: null,
            serviceNotes: '',
            deployNotes: '',
            pickupNotes: '',
            skippedServiceDates: [],
            markJobsReadyForDispatch: true,
        },
        workMode: 'new',
        internalContactId: null,
        billingMethod: 'line_rates',
        billingTerms: createInitialBillingTermsFormValues(),
    }
}

export type JobRequirementType = 'worker' | 'rental_asset'

export type JobDetailLocation = {
    id: string
    name: string
    latitude: number
    longitude: number
    notes: string | null
    addressText?: string | null
}

export type JobDetailComment = {
    id: string
    content: string
    createdAt: string
    createdById: string
    createdByName: string
    updatedAt: string | null
    updatedById: string | null
    updatedByName: string | null
}

export type JobDetailRequirement = {
    id: string
    requirementType: JobRequirementType
    quantity: number
    rentalAssetTypeId: string | null
    rentalAssetTypeName: string | null
    workerTypeId?: string
    billingRate: string | null
    billingType: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'flat' | 'per_load' | null
    estimatedHours?: number | null
}

export type JobDetailAssignment = {
    id: string
    workerId: string | null
    workerName: string | null
    rentalAssetId: string | null
    rentalAssetName: string | null
    rentalAssetTypeId: string | null
    isThirdParty?: boolean
    unitNumber?: string | null
    hasUnitNumbers?: boolean
    pendingOnRent?: boolean
}

export type JobDetailFinancialLineItem = {
    id: string
    jobRequirementId: string | null
    chargeType: BillableChargeType
    name: string
    description: string
    quantity: number
    unit: string
    unitPrice: number
    totalPrice: number
    invoiceId: string | null
    isSynced: boolean
}

export type FinancialLineItemInput = {
    chargeType?: BillableChargeType
    name: string
    description: string
    quantity: number
    unit: string
    unitPrice: number
}

export type JobDetail = JobSchedule &
    JobLifecycle & {
        id: string
        projectId: string
        name: string
        /** Present when loaded from the jobs API; omitted on some older test fixtures. */
        description?: string | null
        serviceTypeId: string
        serviceTypeName: string
        notes: string | null
        customerId: string
        customerName: string
        displayId: string
        poNumber: string | null
        projectBillingTermsId: string | null
        billingMethod?: ProjectBillingMethod | null
        createdAt: string
        serviceScheduleId: string | null
        scheduleRole: 'deploy' | 'service' | 'pickup' | null
        dateTbc?: boolean
        bookByDay?: boolean
        specificType?: string | null
        plannedEndDate?: string | null
        cancelReason?: string
        didNotHappenAt?: string
        didNotHappenByName?: string
        warnings?: string[]
        projectName?: string | null
        projectJobCount?: number
        orderNumber?: string | null
        repId?: string | null
        repName?: string | null
        projectCreatedById?: string | null
        createdById?: string | null
        isPickup?: boolean
        partialPickup?: PartialPickup | null
        pickupConfirmedAt?: string | null
        pickupConfirmedByName?: string | null
        customerContact: CustomerContactRef | null
        customerContactSource: CustomerContactSource | null
        locations: JobDetailLocation[]
        requirements: JobDetailRequirement[]
        stockEquipmentGroups?: JobStockEquipmentGroup[]
        assignments: JobDetailAssignment[]
        financialLineItems: JobDetailFinancialLineItem[]
        comments: JobDetailComment[]
    }

export type JobRequirementValue = {
    requirementType: JobRequirementType
    quantity: number
    workerTypeId: string | null
    rentalAssetTypeId: string | null
    billingRate: string | null
    billingType: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'flat' | 'per_load' | null
}

export type JobAssignmentValue = {
    workerId: string | null
    rentalAssetId: string | null
}

export type RequirementInput = {
    id?: string
    requirementType: JobRequirementType
    quantity: number
    rentalAssetTypeId: string | null
    workerTypeId?: string
    billingRate: string | null
    billingType: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'flat' | 'per_load' | null
}

export type AssignmentInput = {
    id?: string
    workerId: string | null
    rentalAssetId: string | null
    thirdPartyOfferingId?: string | null
    quantity?: number
    days?: {from: string; until: string | null}
}

export type JobPatchInput = {
    jobName: string
    serviceTypeId: string
    notes: string | null
    customerId: string
    customerContactId?: string | null
    poNumber?: string | null
    projectBillingTermsId?: string | null
    specificType?: string | null
    plannedEndDate?: string | null
}

export type JobScheduleInput = {
    startDate: Date
    startTime: string | null
    endDate: Date | null
    endTime: string | null
}

export type JobEditFormValues = {
    jobName: string
    serviceTypeId: string | null
    notes: string
    customerId: string | null
    locations: JobLocation[]
    requirements: JobRequirementValue[]
    assignments: JobAssignmentValue[]
}

export function createInitialJobEditFormValues(): JobEditFormValues {
    return {
        jobName: '',
        serviceTypeId: null,
        notes: '',
        customerId: null,
        locations: [],
        requirements: [],
        assignments: [],
    }
}
